import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  LineChart,
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
} from "recharts";
import {
  getPositionHistory,
  getTrades,
  getMarketData,
  type PositionHistoryPoint,
  type Trade,
  type MarketDataPoint,
} from "../api/client";
import { format } from "date-fns";
import DateRangeSelector from "../components/DateRangeSelector";
import { type DateRangeKey, getDateRangeStart } from "../utils/dateRange";

interface Props {
  portfolioId: number | null;
}

const fmtUSD = (v: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v);

const fmt4 = (v: number) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(v);

export default function PositionHistoryPage({ portfolioId }: Props) {
  const [searchParams] = useSearchParams();
  const selectedSymbol = searchParams.get("symbol");
  const [history, setHistory] = useState<PositionHistoryPoint[]>([]);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [priceData, setPriceData] = useState<MarketDataPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRangeKey>("3M");

  // Load history + trades + price data when symbol changes
  useEffect(() => {
    if (!portfolioId || !selectedSymbol) return;
    setLoading(true);
    setError(null);
    Promise.all([
      getPositionHistory(portfolioId, selectedSymbol),
      getTrades({ portfolio_id: portfolioId, symbol: selectedSymbol, limit: 1000 }),
      getMarketData({ symbol: selectedSymbol, limit: 1000 }),
    ])
      .then(([histRes, tradesRes, priceRes]) => {
        setHistory(histRes.data);
        setTrades(tradesRes.data);
        setPriceData([...priceRes.data].reverse());
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [portfolioId, selectedSymbol]);

  if (!portfolioId) return <p style={{ color: "#64748b" }}>Select a portfolio.</p>;
  if (!selectedSymbol) return <p style={{ color: "#64748b" }}>No symbol selected. Click a symbol in the Positions or Dashboard page.</p>;

  const startDate = getDateRangeStart(dateRange);

  const histFormatted = history
    .filter((d) => !startDate || d.date >= startDate)
    .map((d) => ({
    ...d,
    dateLabel: format(new Date(d.date), "MMM d, yy"),
    quantity: Number(d.quantity),
    market_value: Number(d.market_value),
    unrealized_pnl: Number(d.unrealized_pnl),
    realized_pnl: Number(d.realized_pnl),
    total_pnl: Number(d.unrealized_pnl) + Number(d.realized_pnl),
  }));

  // Price chart with trade annotations
  const priceFormatted = priceData
    .filter((d) => !startDate || d.date >= startDate)
    .map((d) => ({
    date: d.date,
    dateLabel: format(new Date(d.date), "MMM d, yy"),
    close: d.close != null ? Number(d.close) : null,
  }));

  // Build a set of trade dates for annotation (filtered by date range)
  const tradeDateMap: Record<string, { action: string; quantity: number; price: number }[]> = {};
  trades
    .filter((t) => !startDate || t.trade_date >= startDate)
    .forEach((t) => {
    if (!tradeDateMap[t.trade_date]) tradeDateMap[t.trade_date] = [];
    tradeDateMap[t.trade_date].push({ action: t.action, quantity: Number(t.quantity), price: Number(t.price) });
  });

  return (
    <div>
      <h2 style={{ margin: "0 0 20px", color: "#1e293b" }}>Position History — {selectedSymbol}</h2>

      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {error && <div style={{ color: "#dc2626" }}>Error: {error}</div>}
          {loading && <p>Loading…</p>}

          {selectedSymbol && !loading && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <span style={{ fontWeight: 600, color: "#475569", fontSize: 14 }}>Period:</span>
                <DateRangeSelector value={dateRange} onChange={setDateRange} />
              </div>
              {/* Shares & Market Value (dual Y axis) */}
              <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 20 }}>
                <h3 style={{ margin: "0 0 16px", color: "#1e293b" }}>{selectedSymbol} — Shares &amp; Market Value</h3>
                {histFormatted.length === 0 ? (
                  <p style={{ color: "#94a3b8" }}>No position history for this symbol.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <ComposedChart data={histFormatted} margin={{ top: 5, right: 80, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="dateLabel" tick={{ fontSize: 11 }} />
                      <YAxis
                        yAxisId="shares"
                        tickFormatter={fmt4}
                        tick={{ fontSize: 11 }}
                        width={70}
                        domain={["auto", "auto"]}
                        label={{ value: "Shares", angle: -90, position: "insideLeft", offset: -10, style: { fontSize: 11, fill: "#8b5cf6" } }}
                      />
                      <YAxis
                        yAxisId="value"
                        orientation="right"
                        tickFormatter={fmtUSD}
                        tick={{ fontSize: 11 }}
                        width={80}
                        domain={["auto", "auto"]}
                        label={{ value: "Market Value", angle: 90, position: "insideRight", offset: -10, style: { fontSize: 11, fill: "#3b82f6" } }}
                      />
                      <Tooltip
                        formatter={(v: number | undefined, name: string | undefined) => [
                          v != null ? (name === "Shares" ? fmt4(v) : fmtUSD(v)) : "—",
                          name ?? "",
                        ]}
                      />
                      <Legend />
                      <Line yAxisId="shares" type="stepAfter" dataKey="quantity" stroke="#8b5cf6" strokeWidth={2} dot={false} name="Shares" />
                      <Line yAxisId="value" type="monotone" dataKey="market_value" stroke="#3b82f6" strokeWidth={2} dot={false} name="Market Value" />
                    </ComposedChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Stacked Realized + Unrealized P&L */}
              <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 20 }}>
                <h3 style={{ margin: "0 0 16px", color: "#1e293b" }}>{selectedSymbol} — P&amp;L Breakdown</h3>
                {histFormatted.length === 0 ? (
                  <p style={{ color: "#94a3b8" }}>No data.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <ComposedChart data={histFormatted} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="dateLabel" tick={{ fontSize: 11 }} />
                      <YAxis tickFormatter={fmtUSD} tick={{ fontSize: 11 }} width={90} />
                      <Tooltip formatter={(v: number | undefined) => v != null ? fmtUSD(v) : "—"} />
                      <Legend />
                      <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="4 4" />
                      <Area
                        type="monotone"
                        dataKey="realized_pnl"
                        stackId="pnl"
                        stroke="#f59e0b"
                        fill="#fef3c7"
                        strokeWidth={1.5}
                        dot={false}
                        name="Realized P&L"
                      />
                      <Area
                        type="monotone"
                        dataKey="unrealized_pnl"
                        stackId="pnl"
                        stroke="#10b981"
                        fill="#d1fae5"
                        strokeWidth={1.5}
                        dot={false}
                        name="Unrealized P&L"
                      />
                      <Line
                        type="monotone"
                        dataKey="total_pnl"
                        stroke="#3b82f6"
                        strokeWidth={2.5}
                        dot={false}
                        name="Total P&L"
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Price chart with trade annotations */}
              <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 20 }}>
                <h3 style={{ margin: "0 0 16px", color: "#1e293b" }}>{selectedSymbol} — Price Chart with Trades</h3>
                {priceFormatted.length === 0 ? (
                  <p style={{ color: "#94a3b8" }}>
                    No market data available. Fetch it via <strong>Market Data</strong> → Alpha Vantage.
                  </p>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={priceFormatted} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="dateLabel" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 11 }} width={80} domain={["auto", "auto"]} />
                      <Tooltip
                        content={({ active, payload, label }) => {
                          if (!active || !payload || payload.length === 0) return null;
                          const dataPoint = payload[0].payload as { date: string; close: number | null };
                          const tradesOnDay = tradeDateMap[dataPoint.date] ?? [];
                          return (
                            <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6, padding: "8px 12px", fontSize: 12 }}>
                              <div style={{ fontWeight: 600 }}>{label}</div>
                              {dataPoint.close != null && <div>Close: {fmtUSD(dataPoint.close)}</div>}
                              {tradesOnDay.map((t, i) => (
                                <div key={i} style={{ color: t.action === "BUY" ? "#16a34a" : "#dc2626", marginTop: 4 }}>
                                  {t.action} {fmt4(t.quantity)} @ {fmtUSD(t.price)}
                                </div>
                              ))}
                            </div>
                          );
                        }}
                      />
                      <Line type="monotone" dataKey="close" stroke="#3b82f6" strokeWidth={2} dot={false} name="Close" connectNulls />
                      {/* Trade day reference lines */}
                      {Object.entries(tradeDateMap).map(([tradeDate, ts]) => {
                        const action = ts[0]?.action;
                        const label = priceFormatted.find((d) => d.date === tradeDate)?.dateLabel ?? tradeDate;
                        return (
                          <ReferenceLine
                            key={tradeDate}
                            x={label}
                            stroke={action === "BUY" ? "#16a34a" : "#dc2626"}
                            strokeDasharray="4 4"
                            strokeWidth={1.5}
                          />
                        );
                      })}
                    </LineChart>
                  </ResponsiveContainer>
                )}
                {Object.keys(tradeDateMap).length > 0 && (
                  <div style={{ marginTop: 8, fontSize: 12, color: "#64748b" }}>
                    <span style={{ color: "#16a34a", fontWeight: 600 }}>— BUY</span>
                    {" · "}
                    <span style={{ color: "#dc2626", fontWeight: 600 }}>— SELL</span>
                    {" trade dates shown as vertical reference lines"}
                  </div>
                )}
              </div>
            </>
          )}
      </div>
    </div>
  );
}
